import React from 'react';
import { Options, AssetOwnerEmailSearchFormInitialValues } from 'types';
import { FormError } from 'components';
import { Filter16, Close16 } from '@carbon/icons-react';
import {
  DatePicker,
  DatePickerInput,
  Button,
  Form,
  TextInput,
  Dropdown,
  MultiSelect
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { SearchAOEmailsSchema } from 'utilities/validation';
import Utils from 'utilities/helper.util';

const emailSentOption: Options = [
  {
    label: 'Today',
    value: 'today'
  },
  {
    label: 'Last 7 days',
    value: '7'
  },
  {
    label: 'Last 30 days',
    value: '30'
  },
  {
    label: 'Custom range ...',
    value: 'custom'
  }
];

export const statusOption: Options = [
  {
    label: 'Bounced',
    value: 'bounce'
  },
  {
    label: 'Clicked',
    value: 'click'
  },
  {
    label: 'Deferred',
    value: 'deferred'
  },
  {
    label: 'Delivered',
    value: 'delivered'
  },
  {
    label: 'Dropped',
    value: 'dropped'
  },
  {
    label: 'Opened',
    value: 'open'
  },
  {
    label: 'Processed',
    value: 'processed'
  },
  {
    label: 'Spamreport',
    value: 'spamreport'
  },
  {
    label: 'Unsubscribed',
    value: 'unsubscribe'
  }
];

const getDateOptionItem = (value: string) => {
  return emailSentOption.find((o) => value === o.value);
};

const getStatusOptionItem = (value: string[]) => {
  return statusOption.filter((o) => value.includes(o.value));
};

const ApplyFilterForm: React.FC<Props> = ({
  handleFilter,
  filterValues,
  resetValues,
  handleOpenCloseFilter,
  isRefferal,
  parentOverflow
}) => {
  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm
  } = useFormik({
    enableReinitialize: true,
    initialValues: filterValues,
    validationSchema: SearchAOEmailsSchema,
    onSubmit: (values: any) => {
      handleFilter(values);
    }
  });

  const handleResetFilter = () => {
    resetValues();
    resetForm();
  };

  const handleDropdownChange = ({ selectedItem }: any) => {
    if (selectedItem?.value) {
      switch (selectedItem.value) {
        case 'today': {
          const today = Utils.getNewDate().startOf('day');
          setFieldValue('dateRange', selectedItem.value);
          setFieldValue('createdAfter', today.utc().format());
          setFieldValue('createdBefore', '');
          break;
        }
        case '7':
        case '30': {
          const date = Utils.getDateBeforeDays(
            Number(selectedItem.value)
          ).startOf('day');
          setFieldValue('dateRange', selectedItem.value);
          setFieldValue('createdAfter', date.utc().format());
          setFieldValue('createdBefore', '');
          break;
        }
        case 'custom': {
          setFieldValue('dateRange', selectedItem.value);
          setFieldValue('createdAfter', '');
          setFieldValue('createdBefore', '');
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const handleStatusChange = ({ selectedItems }: { selectedItems: any[] }) => {
    const values = selectedItems.map((i) => i.value);
    setFieldValue('status', values);
  };

  const handleDateChange = (key: string, value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      const updatedValue = value[0];
      let dateToSet = null;
      if (key === 'createdAfter') {
        dateToSet = Utils.getNewDate(updatedValue)
          .startOf('day')
          .utc()
          .format();
      } else if (key === 'createdBefore') {
        dateToSet = Utils.getNewDate(updatedValue).endOf('day').utc().format();
      } else {
        dateToSet = Utils.getNewDate(updatedValue).utc().format();
      }
      setFieldValue(key, dateToSet);
    }
  };

  const translateOptionLabel = (item: any) => {
    const { createdAfter, createdBefore, dateRange } = values;

    return item.value === 'custom' &&
      dateRange === 'custom' &&
      createdAfter &&
      createdBefore
      ? `${Utils.toLocalDateFormat(createdAfter)} – ${Utils.toLocalDateFormat(
          createdBefore
        )}`
      : item.label;
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    setField && setFieldValue(key, Utils.getNewDate(val[0]), true);
    handleDateChange(key, val);
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  return (
    <div
      className={`search-filter-panel-wrapper ${
        parentOverflow ? 'parent-overflow' : ''
      }`}
    >
      <div
        className={'search-filter-panel-button'}
        onClick={() => handleOpenCloseFilter(false)}
      >
        <Filter16 />
      </div>
      <section className="search-filter-panel-main">
        <Form>
          <div className="search-filter-panel-form-wrapper">
            <div className="heading">
              <p>Filter the view</p>
              <Button
                kind="ghost"
                size="sm"
                onClick={() => handleOpenCloseFilter(false)}
              >
                <Close16 />
              </Button>
            </div>
            <div className="search-filter-panel-date-container">
              <div className="dropdown-container">
                <Dropdown
                  ariaLabel="Date range"
                  id="email-sent-date"
                  items={emailSentOption}
                  label="Choose an option"
                  titleText="Date range"
                  onChange={handleDropdownChange}
                  itemToString={(item) => translateOptionLabel(item)}
                  initialSelectedItem={getDateOptionItem(values.dateRange)}
                />
                {values.dateRange === 'custom' && (
                  <div className="date-picker-container">
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) => {
                        onChangeDate('createdAfter', val, false);
                      }}
                      onClose={onCalendarClose}
                      value={
                        values.createdAfter
                          ? Utils.toLocalDateFormat(values.createdAfter)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="createdAfter"
                        name="createdAfter"
                        data-testid="createdAfter"
                        labelText="From"
                        placeholder="dd/mm/yyyy"
                        size={'sm'}
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) => {
                        onChangeDate('createdBefore', val, true);
                      }}
                      onClose={onCalendarClose}
                      value={
                        values.createdBefore
                          ? Utils.toLocalDateFormat(values.createdBefore)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="createdBefore"
                        name="createdBefore"
                        labelText="To"
                        data-testid="createdBefore"
                        placeholder="dd/mm/yyyy"
                        size={'sm'}
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                  </div>
                )}
              </div>
              {touched && errors.createdBefore && (
                <FormError>{errors.createdBefore}</FormError>
              )}
            </div>
            <div className="search-filter-panel-status-container">
              <div className="dropdown-container">
                <MultiSelect
                  id="email-status"
                  items={statusOption}
                  label="Choose an option"
                  titleText="Status"
                  onChange={handleStatusChange}
                  initialSelectedItems={getStatusOptionItem(values.status)}
                />
              </div>
            </div>
            <div className="search-filter-panel-input-container">
              {isRefferal ? (
                <>
                  <div className="filter-input">
                    <TextInput
                      id="referralSequenceNumber"
                      name="externalId"
                      labelText="Referral sequence number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.externalId}
                      placeholder="Your input"
                      data-testid="referralSequenceNumber"
                    />
                    {touched && errors.externalId && (
                      <FormError>{errors.externalId}</FormError>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="filter-input">
                    <TextInput
                      id="subjectContains"
                      name="subject"
                      labelText="Subject contains"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.subject}
                      placeholder="Your input"
                      data-testid="subjectContains"
                    />
                    {touched && errors.subject && (
                      <FormError>{errors.subject}</FormError>
                    )}
                  </div>
                  <div className="filter-input">
                    <TextInput
                      id="referralSequenceNumber"
                      name="externalId"
                      labelText="Referral sequence number is"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.externalId}
                      placeholder="Your input"
                      data-testid="referralSequenceNumber"
                    />
                    {touched && errors.externalId && (
                      <FormError>{errors.externalId}</FormError>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="search-filter-panel-form-button-container">
            <Button kind="ghost" size="field" onClick={handleResetFilter}>
              Reset Filters
            </Button>
            <Button
              size="field"
              disabled={Object.keys(errors).length > 0}
              onClick={handleSubmit}
            >
              Apply
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
};

type Props = {
  handleFilter: Function;
  filterValues: AssetOwnerEmailSearchFormInitialValues;
  resetValues: Function;
  handleOpenCloseFilter: Function;
  isRefferal: boolean;
  parentOverflow: boolean;
};

export default ApplyFilterForm;
